import { Tooltip } from '@affine/component';
import { WorkbenchLink } from '@affine/core/modules/workbench';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { NavigationRailCollapse } from '../navigation-rail-collapse';
import * as styles from './ui-redesign-rail.css';

export interface NavigationRailLinkItem {
  active: boolean;
  icon: ReactNode;
  label: string;
  to: string;
}

interface NavigationRailGroupProps {
  active: boolean;
  ariaLabel: string;
  children: ReactNode;
  expanded?: boolean;
  primary: ReactNode;
  revealWhenPopulated?: boolean;
  testId: string;
}

/** Shared expandable rail shell used by navigation items with sub-destinations. */
export function NavigationRailGroup({
  active,
  ariaLabel,
  children,
  expanded,
  primary,
  revealWhenPopulated = false,
  testId,
}: NavigationRailGroupProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [populated, setPopulated] = useState(false);

  useEffect(() => {
    if (!revealWhenPopulated) return;
    const content = contentRef.current;
    if (!content) return;

    const updatePopulated = () => {
      setPopulated(
        content.querySelector('[data-navigation-rail-content="true"]') !== null
      );
    };
    const observer = new MutationObserver(updatePopulated);
    observer.observe(content, { childList: true, subtree: true });
    updatePopulated();
    return () => observer.disconnect();
  }, [revealWhenPopulated]);

  const effectiveExpanded =
    expanded ?? (revealWhenPopulated ? populated : active);

  return (
    <div
      className={styles.nestedNavigationGroup}
      data-active={active}
      data-expanded={effectiveExpanded}
    >
      {primary}
      <NavigationRailCollapse
        as="nav"
        aria-label={ariaLabel}
        contentRef={contentRef}
        expanded={effectiveExpanded}
        data-testid={testId}
      >
        {children}
      </NavigationRailCollapse>
    </div>
  );
}

export function NavigationRailLinks({
  items,
}: {
  items: readonly NavigationRailLinkItem[];
}) {
  return (
    <div className={styles.nestedNavigationLinkList}>
      {items.map(item => (
        <Tooltip content={item.label} key={item.to} side="right">
          <WorkbenchLink
            aria-current={item.active ? 'page' : undefined}
            aria-label={item.label}
            className={styles.nestedNavigationTab}
            data-active={item.active}
            draggable={false}
            to={item.to}
          >
            <span className={styles.nestedNavigationTabIcon}>{item.icon}</span>
            <span className={styles.nestedNavigationTabLabel}>
              {item.label}
            </span>
          </WorkbenchLink>
        </Tooltip>
      ))}
    </div>
  );
}
